import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueService } from '../../services/firebaseService';
import { 
  PlusIcon, 
  UserGroupIcon, 
  TrophyIcon, 
  ArrowRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

export default function LeagueManagerDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [managedLeagues, setManagedLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchManagedLeagues = async () => {
      if (!userProfile?.managedLeagues) {
        setLoading(false);
        return;
      }

      try {
        const leagues = [];
        for (const leagueId of userProfile.managedLeagues) {
          const league = await LeagueService.getLeague(leagueId);
          if (league) {
            leagues.push(league);
          }
        }
        // If only one league, redirect immediately so we never render the list
        if (leagues.length === 1) {
          navigate(`/manage-leagues/${leagues[0].id}`, { replace: true });
          return;
        }
        setManagedLeagues(leagues);
      } catch (error) {
        console.error('Error fetching managed leagues:', error);
        setError('Failed to load your leagues');
      } finally {
        setLoading(false);
      }
    };

    fetchManagedLeagues();
  }, [userProfile, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">League Manager Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Manage your Survivor fantasy leagues and seasons
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}


        {/* Managed Leagues */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Leagues</h2>
          
          {managedLeagues.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leagues yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first Survivor fantasy league.
              </p>
              <div className="mt-6">
                <Link
                  to="/manage-leagues/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create League
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {managedLeagues.map((league) => (
                <div key={league.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{league.name}</h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-sm text-gray-600">
                        <UserGroupIcon className="w-4 h-4 mr-2" />
                        Max {league.maxTeams} teams
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {league.isPrivate ? 'Private' : 'Public'} league
                      </div>
                    </div>

                    <Link
                      to={`/manage-leagues/${league.id}`}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-survivor-700 bg-survivor-100 hover:bg-survivor-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                    >
                      Manage
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create New League - Less Prominent */}
        <div className="text-center">
          <Link
            to="/manage-leagues/create"
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Create new League
          </Link>
        </div>
      </div>
    </div>
  );
}
